// ============================================================================
// ui/shell/marquee.js — "this label is cut off, here is the rest of it"
// ============================================================================
// A clipped label that reads itself out, on a loop, and ONLY when it genuinely
// overflows. Ryan 2026-08-09, first for the phone's PREV/NEXT step buttons and
// then for the product bar beside the tool cluster: *"we need to recognize that
// narrow phone screens might result in the words on this bar now truncating, so
// we need to have them auto-scroll in the same way they do on the left and right
// back/next pills in the slide-up menu header."*
//
// ── THE SHAPE IT NEEDS ──────────────────────────────────────────────────────
// Two elements, always:
//   <span class="…__label">            the WINDOW — overflow:hidden, nowrap
//     <span class="…__labeltext">…</span>   the TEXT — travels
// The window gets `.is-scrolling` and a `--wd-nav-shift` custom property; the
// stylesheet does the rest. Both consumers share those two names deliberately —
// see the rules by `@keyframes wd-navscroll` in weight-designer.css, which every
// new consumer joins rather than copies (`match-shared-ui-by-sharing-rules`).
//
// ⚠ MEASURE FROM REST. The class flips the inner span to `inline-block` and
// translates it, so reading while it is applied measures a box mid-animation
// against a window that has since changed. It cost a 28px shift for a 16px
// overflow the first time. The class comes off, the measurement happens, the
// class goes back on.
//
// ⚠ THE INNER SPAN IS `inline` UNTIL IT SCROLLS, and that is why this cannot be
// pure CSS. `text-overflow: ellipsis` works on inline content but skips an
// atomic inline-level box, so making it `inline-block` up front would silently
// kill the ellipsis on every label — including the ones that never scroll — and
// transforms do not apply to non-replaced inline elements, so it cannot simply
// always be a block. Two states, two display types.
// ============================================================================

/**
 * Turn the marquee on or off for one window element, by measuring it.
 *
 * @param {HTMLElement|null} win  the clipping window; its FIRST ELEMENT CHILD is the travelling text
 * @param {boolean} [skip]        force it off without measuring (a hidden or empty control)
 * @returns {boolean} whether it is now scrolling
 */
export function syncMarquee(win, skip = false) {
  const text = win && win.firstElementChild;
  if (!win || !text) return false;
  win.classList.remove('is-scrolling');
  if (skip) {
    win.style.removeProperty('--wd-nav-shift');
    return false;
  }
  /* ⛔ RIGHT EDGE MINUS RIGHT EDGE, NOT WIDTH MINUS WIDTH — and the difference is visible (Ryan
     2026-08-09, on "Competition Urethane": *"when it scrolls so you can see the full word
     'urethane', it doesn't move far enough so the 'e' at the very end is cut off still... it's only
     half cut off, but still, that's sloppy"*).
       Both rects are border-box, so a width difference silently assumes the text STARTS at the
     window's left edge. It does not: `.wd-product-tab-label` carries `padding: 0 2px`, so the run
     begins 2px in and every width-based sum was 2px short — which lands mid-glyph on the last
     letter and looks exactly like sloppiness rather than like arithmetic.
       The distance the text must travel is, definitionally, how far its right edge sits past the
     window's. That is true whatever padding, border or leading offset either box has, so it cannot
     be re-broken by a future style change the way the width form was.
     ⚠ `ceil`, never `round`: rounding down by half a pixel stops half a hair short, which is the
     same complaint one order of magnitude smaller. Erring long is invisible — the text simply
     parks a hair past the edge, inside the clip. */
  const over = Math.ceil(text.getBoundingClientRect().right - win.getBoundingClientRect().right);
  if (over > 1) {
    win.style.setProperty('--wd-nav-shift', `${-over}px`);
    win.classList.add('is-scrolling');
    return true;
  }
  win.style.removeProperty('--wd-nav-shift');
  return false;
}

/**
 * Re-measure whenever `box` changes size, debounced.
 *
 * ⛔ THIS IS NOT OPTIONAL POLISH — it is most of the times the measurement matters. The first read
 * lands one frame after the labels are written, and on a phone that frame is usually still inside
 * the sheet's 0.55s open, so the window is narrower than it will be and the marquee is set up to
 * travel further than the text actually overflows. A rotation and a late web font do the same thing
 * later. Debounced because a transition resizes the box on every frame of it and each run restarts
 * the animation: one settled read is worth sixty unsettled ones.
 *
 * @returns {() => void} unobserve
 */
export function observeMarquee(box, sync, wait = 140) {
  if (typeof ResizeObserver !== 'function' || !box) return () => {};
  let settle = null;
  const ro = new ResizeObserver(() => {
    clearTimeout(settle);
    settle = setTimeout(sync, wait);
  });
  ro.observe(box);
  return () => { clearTimeout(settle); ro.disconnect(); };
}
